import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router';

import { apiGet, apiPost } from '../lib/api';

type ProjectStatus = 'draft' | 'active' | 'paused' | 'completed' | 'archived';

interface Project {
  id: string;
  name: string;
  status: ProjectStatus;
  billing_type: string;
  billing_cycle: string;
  client_id: string;
  markup_pct: number | null;
  created_at: string;
}

interface ClientOption {
  id: string;
  display_name: string;
  default_markup_pct: number;
}

export function ProjectsPage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | 'all'>('all');
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [formClientId, setFormClientId] = useState('');
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formBillingType, setFormBillingType] = useState('usage_based');
  const [formBillingCycle, setFormBillingCycle] = useState('monthly');
  const [formMarkup, setFormMarkup] = useState('');
  const [formCap, setFormCap] = useState('');
  const [formFixedTotal, setFormFixedTotal] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    void loadData();
  }, []);

  async function loadData(): Promise<void> {
    try {
      const [projectData, clientData] = await Promise.all([
        apiGet<Project[]>('/api/projects'),
        apiGet<ClientOption[]>('/api/clients'),
      ]);
      setProjects(projectData);
      setClients(clientData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    }
  }

  useEffect(() => {
    if (formClientId && clients.length > 0) {
      const selected = clients.find((c) => c.id === formClientId);
      if (selected) {
        setFormMarkup(String(Math.round(selected.default_markup_pct * 100)));
      }
    }
  }, [formClientId, clients]);

  async function createProject(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!formClientId || !formName.trim()) {
      setError('Client and project name are required');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const markupPct = formMarkup ? Number(formMarkup) / 100 : undefined;
      const cap = formCap ? Number(formCap) : undefined;
      const fixedTotal = formFixedTotal ? Number(formFixedTotal) : undefined;
      await apiPost('/api/projects', {
        client_id: formClientId,
        name: formName.trim(),
        description: formDesc.trim() || undefined,
        billing_type: formBillingType,
        billing_cycle: formBillingCycle,
        ...(markupPct !== undefined ? { markup_pct: markupPct } : {}),
        ...(cap !== undefined ? { monthly_cap_usd: cap } : {}),
        ...(fixedTotal !== undefined ? { fixed_total_usd: fixedTotal } : {}),
      }, { successMessage: 'Project created' });
      setFormName('');
      setFormDesc('');
      setFormClientId('');
      setShowForm(false);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project');
    } finally {
      setCreating(false);
    }
  }

  const clientNameOf = (id: string): string => clients.find((c) => c.id === id)?.display_name ?? id;

  const visible = useMemo(
    () => projects.filter((p) => statusFilter === 'all' || p.status === statusFilter),
    [projects, statusFilter],
  );

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Projects</h1>
          <p className="mt-1 text-sm text-slate-600">Manage all projects. Click a project for full details, invoicing, and usage.</p>
        </div>
        <div className="flex gap-2">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as ProjectStatus | 'all')} className="rounded-md border border-slate-300 px-3 py-2 text-sm">
            <option value="all">All statuses</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
            <option value="archived">Archived</option>
          </select>
          <button type="button" onClick={() => setShowForm((p) => !p)} className="rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700">
            {showForm ? 'Cancel' : 'New Project'}
          </button>
        </div>
      </div>

      {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}

      {showForm ? (
        <form onSubmit={createProject} className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
          <h3 className="text-sm font-semibold text-slate-800">New Project</h3>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-sm font-medium">
              Client *
              <select value={formClientId} onChange={(e) => setFormClientId(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" required>
                <option value="">Select client</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.display_name}</option>)}
              </select>
            </label>
            <label className="text-sm font-medium">
              Project Name *
              <input value={formName} onChange={(e) => setFormName(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" required />
            </label>
            <label className="text-sm font-medium md:col-span-2">
              Description
              <textarea value={formDesc} onChange={(e) => setFormDesc(e.target.value)} rows={2} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="text-sm font-medium">
              Billing Type
              <select value={formBillingType} onChange={(e) => setFormBillingType(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
                <option value="usage_based">Usage Based</option>
                <option value="fixed_price">Fixed Price</option>
                <option value="time_material">Time &amp; Material</option>
              </select>
            </label>
            <label className="text-sm font-medium">
              Billing Cycle
              <select value={formBillingCycle} onChange={(e) => setFormBillingCycle(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
                <option value="monthly">Monthly</option>
                <option value="milestone">Milestone</option>
                <option value="on_demand">On Demand</option>
              </select>
            </label>
            <label className="text-sm font-medium">
              Markup % (0–100)
              <input type="number" min="0" max="100" step="1" value={formMarkup} onChange={(e) => setFormMarkup(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="text-sm font-medium">
              Monthly Cap (USD)
              <input type="number" min="0" step="0.01" value={formCap} onChange={(e) => setFormCap(e.target.value)} placeholder="Optional" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            </label>
            {formBillingType === 'fixed_price' ? (
              <label className="text-sm font-medium">
                Fixed Total (USD)
                <input type="number" min="0" step="0.01" value={formFixedTotal} onChange={(e) => setFormFixedTotal(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
              </label>
            ) : null}
          </div>
          <button type="submit" disabled={creating} className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60">
            {creating ? 'Creating...' : 'Create Project'}
          </button>
        </form>
      ) : null}

      <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-slate-600">
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Client</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Billing</th>
              <th className="px-3 py-2">Cycle</th>
              <th className="px-3 py-2">Markup</th>
              <th className="px-3 py-2">Created</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((p) => (
              <tr key={p.id} className="border-b border-slate-100 last:border-0">
                <td className="px-3 py-2">
                  <Link to={`/projects/${p.id}`} className="font-medium text-brand-700 hover:underline">{p.name}</Link>
                </td>
                <td className="px-3 py-2">
                  <Link to={`/clients/${p.client_id}`} className="text-brand-700 hover:underline">{clientNameOf(p.client_id)}</Link>
                </td>
                <td className="px-3 py-2"><StatusBadge status={p.status} /></td>
                <td className="px-3 py-2 capitalize">{p.billing_type.replace('_', ' ')}</td>
                <td className="px-3 py-2 capitalize">{p.billing_cycle.replace('_', ' ')}</td>
                <td className="px-3 py-2">{p.markup_pct !== null ? `${Math.round(p.markup_pct * 100)}%` : '-'}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{shortDate(p.created_at)}</td>
              </tr>
            ))}
            {visible.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500">No projects found.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge(props: { status: ProjectStatus }) {
  const styles: Record<ProjectStatus, string> = {
    draft: 'bg-slate-100 text-slate-700',
    active: 'bg-emerald-50 text-emerald-700',
    paused: 'bg-amber-50 text-amber-700',
    completed: 'bg-blue-50 text-blue-700',
    archived: 'bg-slate-100 text-slate-500',
  };
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${styles[props.status]}`}>{props.status}</span>;
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().slice(0, 10);
}
